import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, TrendingUp, Zap, Star, CheckCircle2, ArrowRight, ShieldCheck } from 'lucide-react';
import { useGeoPricingContext } from '../../context/GeoPricingContext';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { toast } from 'sonner';

// ── Tipos ──────────────────────────────────────────────────────────
interface UpgradePreview {
    current_plan: string;
    new_plan: string;
    current_plan_label: string;
    new_plan_label: string;
    remaining_messages: number;
    price_per_message: number;
    credit_amount: number;
    new_plan_price: number;
    charge_today: number;
    currency?: string;
    new_messages: number;
    new_period_end: string;
}

interface UpgradePlan {
    id: string;
    label: string;
    price: number;
    messages: number;
    icon: React.ReactNode;
    highlight?: boolean;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentPlan: string;
    onUpgradeSuccess: () => void;
}

// ── Config de jerarquía ───────────────────────────────────────────────
const PLAN_HIERARCHY = ['basic_200', 'standard_1000', 'premium_3000'];

const getPlanIcon = (planId: string) => {
    if (planId === 'standard_1000') return <Zap className="w-5 h-5 text-sky-400" />;
    if (planId === 'premium_3000') return <Star className="w-5 h-5 text-amber-400" />;
    return <TrendingUp className="w-5 h-5 text-[#1CB3B3]" />;
};

const getPlanLabel = (planId: string) => {
    if (planId === 'basic_200') return 'Básico';
    if (planId === 'standard_1000') return 'Estándar';
    if (planId === 'premium_3000') return 'Avanzado';
    return planId;
};

// ── Componente ──────────────────────────────────────────────────────
export function UpgradeSubscriptionModal({ open, onOpenChange, currentPlan, onUpgradeSuccess }: Props) {
    const { geoConfig } = useGeoPricingContext();
    const [step, setStep] = useState<'select' | 'preview' | 'confirm'>('select');
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [preview, setPreview] = useState<UpgradePreview | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [upgrading, setUpgrading] = useState(false);

    const currency = geoConfig?.pricing?.currency || 'COP';

    const formatPrice = (val: number) => {
        return val.toLocaleString(currency === 'COP' ? 'es-CO' : 'en-US', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: currency === 'COP' ? 0 : 2
        });
    };

    // Construir planes disponibles desde la geoConfig dinámica
    const availablePlans: UpgradePlan[] = [];
    if (geoConfig?.pricing?.plans) {
        const currentIdx = PLAN_HIERARCHY.indexOf(currentPlan);
        
        Object.entries(geoConfig.pricing.plans).forEach(([id, data]: [string, any]) => {
            const planIdx = PLAN_HIERARCHY.indexOf(id);
            if (planIdx > currentIdx) {
                availablePlans.push({
                    id,
                    label: getPlanLabel(id),
                    price: data.price,
                    messages: data.messages,
                    icon: getPlanIcon(id),
                    highlight: id === 'premium_3000'
                });
            }
        });
        
        // Ordenar por jerarquía
        availablePlans.sort((a, b) => PLAN_HIERARCHY.indexOf(a.id) - PLAN_HIERARCHY.indexOf(b.id));
    }

    // Reset cuando se cierra
    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setStep('select');
                setSelectedPlan(null);
                setPreview(null);
            }, 300);
        }
    }, [open]);

    const handleSelectPlan = async (planId: string) => {
        setSelectedPlan(planId);
        setStep('preview');
        setLoadingPreview(true);
        try {
            const data = await recurringPaymentsApi.getUpgradePreview(planId);
            setPreview(data);
        } catch (err: any) {
            toast.error('Error al calcular el prorrateo', {
                description: err.response?.data?.detail || 'Intenta de nuevo.',
            });
            setStep('select');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleConfirmUpgrade = async () => {
        if (!selectedPlan) return;
        setUpgrading(true);
        try {
            await recurringPaymentsApi.upgradeSubscription(selectedPlan);
            toast.success('¡Plan mejorado exitosamente! 🎉', {
                description: `Ya tienes acceso a ${preview?.new_messages?.toLocaleString('es-CO')} mensajes.`,
            });
            onOpenChange(false);
            onUpgradeSuccess();
        } catch (err: any) {
            toast.error('El upgrade falló', {
                description: err.response?.data?.detail || 'Verifica tu método de pago e intenta de nuevo.',
            });
        } finally {
            setUpgrading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-lg shadow-xl">
            <DialogHeader>
                <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#1CB3B3]" />
                    Mejorar suscripción
                </DialogTitle>

                <DialogDescription className="text-slate-500">
                    {step === 'select'
                        ? 'Selecciona el plan al que deseas mejorar.'
                        : 'Revisa el costo de hoy antes de confirmar.'}
                </DialogDescription>
            </DialogHeader>

                {/* ── PASO 1: Selección de plan ── */}
                {step === 'select' && (
                    <div className="grid gap-3 mt-4">
                        {availablePlans.map(plan => (
                            <button
                                key={plan.id}
                                onClick={() => handleSelectPlan(plan.id)}
                                className={`
                                    relative flex items-center gap-4 p-4 rounded-xl border text-left
                                    transition-all duration-200 group
                                    ${plan.highlight
                                        ? 'border-[#E5D4FA] bg-[#FBF8FF] hover:bg-[#F5EEFF] hover:border-[#CDAAF5]'
                                        : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-[#1CB3B3]/50'
                                    }
                                `}
                            >
                                <div
                                    className={`
                                        p-2.5 rounded-lg flex-shrink-0
                                        ${plan.highlight
                                            ? 'bg-[#F0E7FC]'
                                            : 'bg-[#E8F7F7]'
                                        }
                                    `}
                                >
                                    {plan.icon}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">

                                        <p className="font-bold text-slate-900">
                                            {plan.label}
                                        </p>

                                        {plan.highlight && (
                                            <span className="text-[9px] font-bold uppercase tracking-widest text-[#8B4FC6] bg-[#F0E7FC] px-1.5 py-0.5 rounded-full">
                                                Recomendado
                                            </span>
                                        )}

                                    </div>

                                    <p className="text-sm text-slate-500">
                                        {plan.messages.toLocaleString('es-CO')} recordatorios automáticos / mes
                                    </p>
                                </div>

                                <div className="text-right flex-shrink-0">
                                    <p className="font-black text-slate-900">
                                        {formatPrice(plan.price)}
                                    </p>

                                    <p className="text-xs text-slate-500">
                                        / mes
                                    </p>
                                </div>

                                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1CB3B3] transition-colors flex-shrink-0" />

                            </button>
                        ))}
                    </div>
                )}

                {/* ── PASO 2: Preview de prorrateo ── */}
                {step === 'preview' && (
                    <div className="mt-4">
                        {loadingPreview ? (
                            <div className="flex flex-col items-center gap-3 py-10">
                                <Loader2 className="w-8 h-8 animate-spin text-[#1CB3B3]" />

                                <p className="text-slate-500 text-sm">
                                    Calculando prorrateo...
                                </p>
                            </div>
                        ) : preview ? (
                            <>
                                {/* Resumen visual */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 mb-4">
                                    <p className="text-xs text-slate-500 uppercase font-bold tracking-widest mb-2">
                                        Desglose del cobro de hoy
                                    </p>

                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">
                                            Precio {preview.new_plan_label}
                                        </span>

                                        <span className="text-slate-900 font-semibold">
                                            {formatPrice(preview.new_plan_price)}
                                        </span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                        <span className="text-emerald-600">
                                            Crédito ({preview.remaining_messages} msgs no usados)
                                        </span>

                                        <span className="text-emerald-600 font-semibold">
                                            − {formatPrice(preview.credit_amount)}
                                        </span>
                                    </div>

                                    <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                                        <span className="font-bold text-slate-900">
                                            Total a pagar hoy
                                        </span>

                                        <span className="font-black text-2xl text-[#159A9C]">
                                            {formatPrice(preview.charge_today)}
                                        </span>
                                    </div>
                                </div>

                                {/* Lo que obtendrás */}
                                <div className="bg-[#E8F7F7] border border-[#CDEEEE] rounded-xl p-4 mb-5">
                                    <p className="text-xs text-[#159A9C] uppercase font-bold tracking-widest mb-2">
                                        Lo que obtienes
                                    </p>

                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <CheckCircle2 className="w-4 h-4 text-[#1CB3B3]" />
                                        <span>
                                            {preview.new_messages.toLocaleString('es-CO')} mensajes desde hoy
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm text-slate-700 mt-1.5">
                                        <CheckCircle2 className="w-4 h-4 text-[#1CB3B3]" />

                                        <span>
                                            Próximo cobro: {formatPrice(preview.new_plan_price)} el{' '}
                                            {new Date(preview.new_period_end).toLocaleDateString('es-CO', {
                                                day: 'numeric',
                                                month: 'long'
                                            })}
                                        </span>
                                    </div>
                                </div>

                                {/* Botones */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setStep('select')}
                                        disabled={upgrading}
                                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 transition-all"
                                    >
                                        Volver
                                    </button>

                                    <button
                                        onClick={handleConfirmUpgrade}
                                        disabled={upgrading}
                                        className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-[#1CB3B3] hover:bg-[#169a9a] text-white shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {upgrading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Procesando...
                                            </>
                                        ) : (
                                            <>
                                                <ShieldCheck className="w-4 h-4" />
                                                Pagar y mejorar ahora
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : null}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
